/**
 * Authentication Context
 *
 * Provides global authentication state and methods:
 * - Current user
 * - Login/logout functions
 * - Token management
 * - Data synchronization between localStorage and server
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import WordBank from '../WordBank';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('session_token');
    const user = localStorage.getItem('user');

    if (token && user) {
      try {
        setCurrentUser(JSON.parse(user));
        // Sync server data to localStorage on page load
        WordBank.syncFromServer();
      } catch (error) {
        console.error('Failed to parse user data:', error);
        logout();
      }
    }

    setLoading(false);
  }, []);

  /**
   * Sync server data to localStorage (once per page load)
   */
  // Sync functionality moved to WordBank.js to avoid schema conflicts
  // WordBank.syncFromServer() will handle syncing server data to localStorage

  /**
   * Handle Google OAuth login
   */
  const handleGoogleLogin = async (googleToken) => {
    try {
      // Step 1: Authenticate with backend
      const response = await api.post('/api/auth/google', { token: googleToken });
      const { session_token, user, expires_at } = response.data;

      // Step 2: Save session token and user
      localStorage.setItem('session_token', session_token);
      localStorage.setItem('user', JSON.stringify(user));
      setCurrentUser(user);

      // Step 3: Sync word progress from server to localStorage
      await WordBank.syncFromServer();

      // Step 4: Notify App component to refresh quiz data
      window.dispatchEvent(new CustomEvent('wordbank-synced'));

      return { success: true, user };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  // Migration removed - WordBank will handle all vxDB syncing

  /**
   * Logout
   */
  const logout = async () => {
    try {
      // Notify server (invalidate session)
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local data
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      setCurrentUser(null);
    }
  };

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = () => {
    return !!currentUser && !!localStorage.getItem('session_token');
  };

  const value = {
    currentUser,
    loading,
    isAuthenticated: isAuthenticated(),
    handleGoogleLogin,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export default AuthContext;
